<template>
    <div id="container" ref="container">
        <v-stage ref="stage" :config="stageConfig">
            <v-layer>
                <v-image :config="oldFloorplanConfig" ref="oldFloorplanImage" />
            </v-layer>
            <v-layer ref="layerTransform">
                <v-image :config="newFloorplanConfig" ref="newFloorplanImage" @transformend="handleTransformEnd" />
                <v-transformer
                    ref="transformer"
                    :rotateEnabled="false"
                    :enabledAnchors="['top-left', 'top-right', 'bottom-left', 'bottom-right']"
                    :keepRatio="true"
                />
            </v-layer>
        </v-stage>
    </div>
</template>
<script>
export default {
    name: 'FloorplanOverlay',
    props: {
        oldFloorplan: HTMLImageElement,
        newFloorplan: HTMLImageElement,
        viewpoints: Array,
        sectors: {
            type: Array,
            default: () => {
                return []
            },
        },
    },
    data() {
        return {
            oldFloorplanConfig: {},
            newFloorplanConfig: {},
            stageConfig: {},

            // Floorplan canvas variables
            reloading: false,
            stage: null,
            stageConfig: {},
            mainLayer: null,
            image: null,
            oldFloorplanProportionX: 1,
            oldFloorplanProportionY: 1,
            newFloorplanProportionX: 1,
            newFloorplanProportionY: 1,
            imageNode: null,
            imageAsset: null,
            imgIconNode: null,
            anchorOrigin: null,
            viewpointNodes: [],
            viewpointOrigin: null,
            sectorNodes: [],
            sectorOrigin: null,
            tooltipNode: null,
            dotSize: 10,
            colorMap: {
                white: '#FFFFFF',
                blue: '#1EB3FD',
                primary: '#151517',
                gray: '#808080',
                high: '#FB0335',
                medium: '#FFA800',
                low: '#1EB3FD',
            },
            scale: 1,
            lastPinchDistance: null,
        }
    },
    methods: {
        handleTransformEnd(e) {
            this.newFloorplanConfig.x = e.target.x()
            this.newFloorplanConfig.y = e.target.y()
            this.newFloorplanConfig.scaleX = e.target.scaleX()
            this.newFloorplanConfig.scaleY = e.target.scaleY()
        },
        updateTransformer() {
            const transformerNode = this.$refs.transformer.getNode()
            const stage = transformerNode.getStage()
            const selectedNode = this.$refs.newFloorplanImage.getNode()

            if (selectedNode) {
                transformerNode.attachTo(selectedNode)
            } else {
                transformerNode.detach()
            }

            transformerNode.getLayer().batchDraw()
        },
        setupViewpointOrigin() {
            this.viewpointOrigin = new Konva.Circle({
                radius: this.dotSize,
                strokeWidth: this.dotSize / 3,
                opacity: 0.8,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
            })
            this.viewpointOrigin.cache()
        },
        setupSectorOrigin() {
            this.sectorOrigin = new Konva.Line({
                x: this.oldFloorplanConfig.position.x,
                y: this.oldFloorplanConfig.position.y,
                closed: true,
                stroke: this.colorMap.primary,
                fill: this.colorMap.blue,
                opacity: 0.6,
            })

            this.anchorOrigin = new Konva.Circle({
                radius: 4,
                strokeWidth: 2,
                fill: this.colorMap.blue,
                stroke: this.colorMap.primary,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
            })
        },
        noViewpointSelected() {
            if (this.selectViewpointByProp) {
                return !this.viewpoints.filter((vp) => vp.selected).length
            } else {
                return this.selectedViewpoint == -1
            }
        },
        isViewpointSelected(viewpoint) {
            if (this.selectViewpointByProp) {
                return viewpoint.selected
            } else {
                return this.selectedViewpoint == viewpoint.id
            }
        },
        configViewpoints() {
            if (!this.viewpointOrigin) {
                this.setupViewpointOrigin()
            }
            this.viewpointNodes.forEach((node) => {
                node.destroy()
            })
            this.viewpointNodes = []

            let { x, y } = this.oldFloorplanConfig.position
            let noViewpointSelected = this.noViewpointSelected()
            let scale = 1

            this.viewpoints.forEach((viewpoint, index) => {
                let isSelected = this.isViewpointSelected(viewpoint)

                let clone = this.viewpointOrigin.clone({
                    id: viewpoint.id,
                    x: x + (viewpoint.x / this.oldFloorplan.width) * (this.oldFloorplanConfig.width * this.oldFloorplanConfig.scale.x),
                    y: y + (viewpoint.y / this.oldFloorplan.height) * (this.oldFloorplanConfig.height * this.oldFloorplanConfig.scale.x),
                    scale: { x: scale, y: scale },
                    stroke: this.colorMap.primary,

                    fill: this.colorMap.blue,
                })

                this.viewpointNodes.push(clone)
                this.$refs.oldFloorplanImage.getNode().getLayer().add(clone)
            })

            if (!noViewpointSelected) {
                this.viewpointNodes.forEach((node, index) => {
                    let viewpoint = this.viewpoints.find((vp) => vp.id == node.getId())
                    if (this.isViewpointSelected(viewpoint)) {
                        node.zIndex(this.viewpoints.length)
                    } else {
                        node.zIndex(index + 1)
                    }
                })
            }
        },
        noSectorSelected() {
            if (this.selectSectorByProp) {
                return !this.sectors.filter((sec) => sec.selected).length
            } else {
                return this.selectedSector == -1
            }
        },
        isSectorSelected(sector) {
            if (this.selectSectorByProp) {
                return sector.selected
            } else {
                return this.selectedSector == sector.id
            }
        },
        configSectors() {
            if (!this.sectorOrigin) {
                this.setupSectorOrigin()
            }
            if (this.sectorNodes.length) {
                this.sectorNodes.forEach((node) => {
                    node.destroy()
                })
                this.sectorNodes = []
            }

            let noSectorSelected = this.noSectorSelected()
            this.sectors.forEach((sector) => {
                let clone = this.sectorOrigin.clone({
                    id: sector.id,
                    points: sector.coords.reduce((arr, coord) => {
                        arr.push(coord.x * this.oldFloorplanProportionX, coord.y * this.oldFloorplanProportionY)
                        return arr
                    }, []),
                    stroke: this.isSectorSelected(sector) ? this.colorMap.blue : this.colorMap.primary,
                    strokeWidth: this.isSectorSelected(sector) ? 4 : 2,
                    fill: this.isSectorSelected(sector) ? this.colorMap.white : this.colorMap.blue,
                    perfectDrawEnabled: false,
                })

                this.sectorNodes.push(clone)
                this.$refs.oldFloorplanImage.getNode().getLayer().add(clone)

                if (this.anchorSelectedSector && !noSectorSelected && this.isSectorSelected(sector)) {
                    clone.anchors = []
                    sector.coords.forEach((coord) => {
                        let anchor = this.anchorOrigin.clone({
                            id: 'anchor',
                            x: this.oldFloorplanConfig.position.x + coord.x * this.oldFloorplanProportionX,
                            y: this.oldFloorplanConfig.position.y + coord.y * this.oldFloorplanProportionY,
                        })

                        clone.anchors.push(anchor)
                        this.mainLayer.add(anchor)
                    })
                }
            })
        },
        configImages() {
            let oldScale = 1,
                newScale = 1
            let { clientWidth, clientHeight } = this.$refs.container

            clientHeight -= 30
            clientWidth -= 30

            let oldFWidth = this.oldFloorplan.width,
                oldFHeight = this.oldFloorplan.height

            let newFWidth = this.newFloorplan.width,
                newFHeight = this.newFloorplan.height

            if (oldFWidth * oldScale > clientWidth) {
                oldScale = clientWidth / oldFWidth
            }

            if (oldFHeight * oldScale > clientHeight) {
                oldScale = clientHeight / oldFHeight
            }

            if (newFWidth * newScale > clientWidth) {
                newScale = clientWidth / newFWidth
            }

            if (newFHeight * newScale > clientHeight) {
                newScale = clientHeight / newFHeight
            }

            this.oldFloorplanConfig = {
                image: this.oldFloorplan,
                opacity: 1,
                width: oldFWidth,
                height: oldFHeight,
                scale: { x: oldScale, y: oldScale },
                position: {
                    x: (clientWidth - oldFWidth * oldScale) / 2,
                    y: (clientHeight - oldFHeight * oldScale) / 2,
                },
            }

            this.newFloorplanConfig = {
                image: this.newFloorplan,
                opacity: 0.4,
                width: newFWidth,
                height: newFHeight,
                scale: { x: newScale, y: newScale },
                position: {
                    x: (clientWidth - newFWidth * newScale) / 2,
                    y: (clientHeight - newFHeight * newScale) / 2,
                },
                draggable: true,
            }

            this.oldFloorplanProportionX = (this.oldFloorplanConfig.width * this.oldFloorplanConfig.scale.x) / this.oldFloorplan.width
            this.oldFloorplanProportionY = (this.oldFloorplanConfig.height * this.oldFloorplanConfig.scale.y) / this.oldFloorplan.height

            this.newFloorplanProportionX = (this.newFloorplanConfig.width * this.newFloorplanConfig.scale.x) / this.newFloorplan.width
            this.newFloorplanProportionY = (this.newFloorplanConfig.height * this.newFloorplanConfig.scale.y) / this.newFloorplan.height
        },
        configStage() {
            return new Promise((resolve) => {
                let { clientWidth, clientHeight } = this.$refs.container
                this.stageConfig = {
                    width: clientWidth - 10,
                    height: clientHeight - 10,
                    draggable: this.draggableStage,
                }
                this.$nextTick(() => {
                    this.stage = this.$refs.stage.getStage()
                    resolve()
                })
            })
        },

        // Public Methods
        getProportionalViewpoints() {
            let newViewpoints = []

            let node = this.$refs.newFloorplanImage.getNode()
            let oldNode = this.$refs.oldFloorplanImage.getNode()

            newViewpoints = this.viewpointNodes.map((v) => {
                let x = ((v.x() - node.getPosition().x) / (node.width() * node.scale().x)) * this.newFloorplan.width
                let y = ((v.y() - node.getPosition().y) / (node.height() * node.scale().y)) * this.newFloorplan.height
                return {
                    id: v.getId(),
                    x,
                    y,
                    draggable: true,
                    new: false,
                }
            })

            return newViewpoints
        },

        getProportionalSectors() {
            let newSectors = []

            let node = this.$refs.newFloorplanImage.getNode()
            let oldNode = this.$refs.oldFloorplanImage.getNode()

            newSectors = this.sectorNodes.map((s) => {
                let coords = []
                s.attrs.points.forEach((point, index) => {
                    // se for par, então é um x
                    if (index % 2 == 0) {
                        coords.push({
                            x: ((point + oldNode.getPosition().x - node.getPosition().x) / (node.width() * node.scale().x)) * this.newFloorplan.width,
                            y:
                                ((s.attrs.points[index + 1] + oldNode.getPosition().y - node.getPosition().y) / (node.height() * node.scale().y)) *
                                this.newFloorplan.height,
                        })
                    }
                })

                return {
                    id: s.getId(),
                    coords,
                    draggable: true,
                    new: false,
                }
            })

            return newSectors
        },
    },
    async mounted() {
        await this.configStage()
        this.configImages()
        this.configViewpoints()
        this.configSectors()

        this.updateTransformer()

        this.$nextTick((_) => {
            this.$refs.oldFloorplanImage.getNode().cache()
            this.$refs.oldFloorplanImage.getNode().filters([Konva.Filters.Grayscale])
        })
    },
}
</script>
<style lang="scss" scoped>
@import '@/scss/components/Floorplan/floorplans-overlay.scss';
</style>
